import {
  isThrowingStarAbility, THROWING_STAR_MAGAZINE, THROWING_STAR_RELOAD_MS,
} from '../../../shared/combat/throwingStars';
import { THROWING_STAR_VISUALS } from '../../../config/abilities/throwingStarVisuals.config';

interface AcceptedCast {
  playerId?: string;
  ability_id?: string;
  abilityId?: string;
  timestamp?: string | number;
  __netTrace?: { serverSeq?: number };
}

interface Rack {
  count: number;
  reloadUntil: number | null;
  lastServerSeq?: number;
}

export interface ReloadVisualBeat {
  effectId: 'reload_stars' | 'reload_stars_complete';
  x: number;
  y: number;
  targetId: string;
  abilityId: string;
  duration: number;
}

const MAX_CASTERS = 128;
const MAX_SEEN_CASTS = 1024;

/**
 * Observer-only presentation from accepted server casts. No timers or ammo
 * gameplay state: ready is shown on the next accepted throw after the deadline.
 * A late join has no rack snapshot on the wire, so its first observed rack
 * starts at zero; this cannot recover a caster's existing magazine phase.
 */
export class ThrowingStarReloadVFX {
  private racks = new Map<string, Rack>();
  private seen = new Set<string>();

  observe(data: AcceptedCast, position: { x: number; y: number }, now: number): ReloadVisualBeat[] {
    const caster = data.playerId;
    const abilityId = data.ability_id ?? data.abilityId ?? '';
    if (!caster || !isThrowingStarAbility(abilityId)) return [];
    const seq = data.__netTrace?.serverSeq;
    const hasSeq = typeof seq === 'number' && Number.isSafeInteger(seq) && seq >= 0;
    const timestamp = data.timestamp;
    const hasTimestamp = (typeof timestamp === 'string' && timestamp.length > 0)
      || (typeof timestamp === 'number' && Number.isFinite(timestamp));
    // Without a server identity, retries cannot safely imply another shot.
    if (!hasSeq && !hasTimestamp) return [];
    const key = hasSeq ? `${caster}:seq:${seq}` : JSON.stringify([caster, abilityId, timestamp]);
    if (this.seen.has(key)) return [];
    let rack = this.racks.get(caster);
    if (hasSeq && rack?.lastServerSeq !== undefined && seq <= rack.lastServerSeq) return [];
    this.seen.add(key);
    if (this.seen.size > MAX_SEEN_CASTS) this.seen.delete(this.seen.values().next().value!);
    if (!rack) {
      if (this.racks.size >= MAX_CASTERS) this.racks.delete(this.racks.keys().next().value!);
      rack = { count: 0, reloadUntil: null };
      this.racks.set(caster, rack);
    }
    if (hasSeq) rack.lastServerSeq = seq;
    const beats: ReloadVisualBeat[] = [];
    const beat = (effectId: ReloadVisualBeat['effectId'], duration: number): ReloadVisualBeat => ({
      effectId, x: position.x, y: position.y, targetId: caster, abilityId, duration,
    });
    if (rack.reloadUntil !== null) {
      if (now < rack.reloadUntil) return [];
      beats.push(beat('reload_stars_complete', THROWING_STAR_VISUALS.ready.durationMs));
      rack.reloadUntil = null;
    }
    rack.count++;
    if (rack.count === THROWING_STAR_MAGAZINE) {
      rack.count = 0;
      rack.reloadUntil = now + THROWING_STAR_RELOAD_MS;
      beats.push(beat('reload_stars', THROWING_STAR_RELOAD_MS));
    }
    return beats;
  }

  clear(): void {
    this.racks.clear();
    this.seen.clear();
  }
}
